import { onKeyDown, onKeyUp } from "@vueuse/core";
import { ref } from "vue";
import { elementEditable } from "@/helpers/inputHelpers";

type ModifierKey = "Meta" | "Control" | "Shift" | "Alt";
type ShortcutKey = string;

/**
 * Registers a keyboard shortcut with modifier key support and automatic preventDefault.
 *
 * Simple shortcuts (without modifier) are ignored when the active element is editable.
 * Modifier shortcuts (Ctrl/Cmd+key) work regardless of focus.
 *
 * @example
 * // Cmd+F on Mac, Ctrl+F on Windows - works even in input fields
 * useShortcut(["Control", "f"], () => searchInput.value?.focus())
 *
 * // Simple shortcut - ignored when typing in inputs
 * useShortcut("/", () => searchInput.value?.focus())
 *
 * // Explicit modifier
 * useShortcut(["Shift", "f"], () => doSomething())
 */
export function useShortcut(
	keys: ShortcutKey | [ModifierKey, ShortcutKey],
	callback: () => void,
) {
	// Simple key without modifier - skip if in editable element
	if (typeof keys === "string") {
		onKeyDown(keys, (e) => {
			if (elementEditable(document.activeElement)) {
				return;
			}
			e.preventDefault();
			callback();
		});
		return;
	}

	// Key with modifier - always works
	const [modifier, key] = keys;
	const resolvedModifier =
		modifier === "Control"
			? navigator.platform.startsWith("Mac")
				? "Meta"
				: "Control"
			: modifier;

	const modDown = ref(false);
	const keyDown = ref(false);

	const trigger = (e: KeyboardEvent) => {
		if (modDown.value && keyDown.value) {
			e.preventDefault();
			callback();
		}
	};

	onKeyDown(resolvedModifier, (e) => {
		modDown.value = true;
		trigger(e);
	});

	onKeyUp(resolvedModifier, () => {
		modDown.value = false;
	});

	onKeyDown(key, (e) => {
		keyDown.value = true;
		trigger(e);
	});

	onKeyUp(key, () => {
		keyDown.value = false;
	});
}
